/**
 * Engineering standard resolution.
 *
 * The single place that answers: "may this reading be scored, and against what
 * limits?" Server and device both call it, so a value cannot be judged one way
 * on the phone and another way on sync (same reasoning as `evaluation.ts`).
 *
 * The rule, in one sentence: a measured item is scored only when it carries its
 * own acceptance range, or is linked to an ACTIVE engineering standard that
 * supplies one. Everything else is recorded and shown, never judged.
 */

import {
  SCORING_STANDARD_STATUSES,
  type EngineeringStandardStatus,
  type MeasurementMode,
} from './domain';

/** The subset of an engineering standard needed to resolve scoring. */
export interface StandardRef {
  id: string;
  code: string;
  /** Which revision of this standard is in force. */
  revisionNo: number;
  parameter: string;
  status: EngineeringStandardStatus;
  targetValue: number | null;
  minValue: number | null;
  maxValue: number | null;
  unit: string | null;
  referenceDocument: string | null;
  referenceNumber: string | null;
  /** The document's own revision label, e.g. "Rev C". */
  revision: string | null;
  approvedAt: string | null;
  effectiveDate: string | null;
}

/**
 * The engineering standard frozen against one task on one run.
 *
 * This is what a historical result is evaluated and displayed against, so that
 * a later revision cannot change a past verdict.
 */
export interface RunStandardSnapshot {
  itemId: string;
  standardId: string | null;
  standardCode: string | null;
  revisionNo: number | null;
  standardStatus: string | null;
  targetValue: number | null;
  minValue: number | null;
  maxValue: number | null;
  tolerance: number | null;
  unit: string | null;
  recordOnly: boolean;
  measurementMode: MeasurementMode;
  explanation: string | null;
  referenceDocument: string | null;
  referenceNumber: string | null;
  revisionLabel: string | null;
  /** RUN_START is a real capture; BACKFILL was reconstructed after the fact. */
  source: string;
  resolvedAt: string;
}

/** Human reference for a snapshot, e.g. "ES-0001 Rev 2". */
export function describeSnapshot(snapshot: RunStandardSnapshot | null | undefined): string | null {
  if (!snapshot?.standardCode) return null;
  return snapshot.revisionNo != null
    ? `${snapshot.standardCode} Rev ${snapshot.revisionNo}`
    : snapshot.standardCode;
}

/**
 * Turn a frozen snapshot back into the shape the evaluation engine consumes.
 *
 * why this is the only path used on a run: reading the live standard here would
 * silently re-score history the moment engineering approves a new revision.
 */
export function boundsFromSnapshot(snapshot: RunStandardSnapshot): {
  minValue: number | null;
  maxValue: number | null;
  targetValue: number | null;
  tolerance: number | null;
  unit: string | null;
  recordOnly: boolean;
} {
  return {
    minValue: snapshot.minValue,
    maxValue: snapshot.maxValue,
    targetValue: snapshot.targetValue,
    tolerance: snapshot.tolerance,
    unit: snapshot.unit,
    recordOnly: snapshot.recordOnly,
  };
}

/** The item fields that participate in resolution. */
export interface MeasurableItem {
  itemType: string;
  minValue: number | null;
  maxValue: number | null;
  targetValue: number | null;
  tolerance: number | null;
  recordOnly: boolean;
  unit: string | null;
}

export interface ResolvedStandard {
  /** Effective bounds after applying the standard, if any. */
  minValue: number | null;
  maxValue: number | null;
  targetValue: number | null;
  tolerance: number | null;
  unit: string | null;
  /** True when the engine must not judge the reading. */
  recordOnly: boolean;
  mode: MeasurementMode;
  /** Operator-facing explanation of why it is or is not scored. */
  explanation: string;
  /** The standard actually applied, for display and traceability. */
  appliedStandard: StandardRef | null;
}

function isMeasured(itemType: string): boolean {
  return itemType === 'NUMERIC' || itemType === 'COUNTER';
}

function hasBounds(min: number | null, max: number | null): boolean {
  return min != null || max != null;
}

function formatLimits(
  min: number | null,
  max: number | null,
  target: number | null,
  tolerance: number | null,
  unit: string | null,
): string {
  const u = unit ? ` ${unit}` : '';
  if (target != null && tolerance != null) return `${target} ± ${tolerance}${u}`;
  if (min != null && max != null) return `${min} to ${max}${u}`;
  if (min != null) return `minimum ${min}${u}`;
  if (max != null) return `maximum ${max}${u}`;
  return 'no acceptance range';
}

/**
 * Resolve how a measured item should behave right now.
 *
 * Precedence is deliberate: an ACTIVE engineering standard wins over limits
 * typed into the template, because the standard is the controlled, approved,
 * audited artefact and the template field is not.
 */
export function resolveStandard(
  item: MeasurableItem,
  standard: StandardRef | null | undefined,
): ResolvedStandard {
  const base: ResolvedStandard = {
    minValue: item.minValue,
    maxValue: item.maxValue,
    targetValue: item.targetValue,
    tolerance: item.tolerance,
    unit: item.unit,
    recordOnly: item.recordOnly,
    mode: 'RECORD_ONLY',
    explanation: '',
    appliedStandard: null,
  };

  if (!isMeasured(item.itemType)) {
    return { ...base, recordOnly: false, mode: 'SCORED', explanation: '' };
  }

  const active =
    standard && SCORING_STANDARD_STATUSES.includes(standard.status) ? standard : null;

  if (active && hasBounds(active.minValue, active.maxValue)) {
    const reference = [active.referenceDocument, active.referenceNumber, active.revision]
      .filter(Boolean)
      .join(' ');
    return {
      minValue: active.minValue,
      maxValue: active.maxValue,
      targetValue: active.targetValue ?? item.targetValue,
      tolerance: null,
      unit: active.unit ?? item.unit,
      recordOnly: false,
      mode: 'SCORED',
      explanation: `Scored against ${active.code}${reference ? ` (${reference})` : ''}: ${formatLimits(
        active.minValue,
        active.maxValue,
        active.targetValue,
        null,
        active.unit ?? item.unit,
      )}.`,
      appliedStandard: active,
    };
  }

  // The item defines its own range and is not opted out — score it.
  if (
    !item.recordOnly &&
    (hasBounds(item.minValue, item.maxValue) ||
      (item.targetValue != null && item.tolerance != null))
  ) {
    return {
      ...base,
      recordOnly: false,
      mode: 'SCORED',
      explanation: `Acceptable: ${formatLimits(
        item.minValue,
        item.maxValue,
        item.targetValue,
        item.tolerance,
        item.unit,
      )}.`,
      appliedStandard: null,
    };
  }

  // A standard exists but has not been approved into service yet.
  if (standard) {
    return {
      ...base,
      minValue: null,
      maxValue: null,
      recordOnly: true,
      mode: 'AWAITING_ENGINEERING',
      explanation:
        `Recorded — not scored. Standard ${standard.code} is ${standard.status
          .toLowerCase()
          .replace(/_/g, ' ')} and has not been approved into service.`,
      appliedStandard: null,
    };
  }

  /**
   * The common case for the pilot: the source gives a target with no tolerance
   * and nobody has raised a standard for it.
   *
   * why this is RECORD_ONLY rather than AWAITING_ENGINEERING: "awaiting" is
   * reserved for readings someone is actively working on a tolerance for. A
   * lubrication count of "2 pumps" is not waiting on anything — recording it is
   * the correct and final treatment. Conflating the two would put a permanent
   * backlog number in front of management that never goes down.
   */
  return {
    ...base,
    minValue: null,
    maxValue: null,
    recordOnly: true,
    mode: 'RECORD_ONLY',
    explanation:
      item.targetValue != null
        ? `Recorded — not scored. Standard: ${item.targetValue}${item.unit ? ` ${item.unit}` : ''}. ` +
          'No engineering acceptance tolerance has been configured.'
        : 'Recorded — not scored. No acceptance range is configured for this reading.',
    appliedStandard: null,
  };
}

/** Short badge text for the reading, once one has been entered. */
export function describeReading(
  resolved: ResolvedStandard,
  value: number | null,
): { label: string; tone: 'ok' | 'alarm' | 'info' } {
  if (value == null) return { label: '', tone: 'info' };
  if (resolved.recordOnly) return { label: 'Recorded — not scored', tone: 'info' };

  const belowMin = resolved.minValue != null && value < resolved.minValue;
  const aboveMax = resolved.maxValue != null && value > resolved.maxValue;

  return belowMin || aboveMax
    ? { label: 'Out of standard', tone: 'alarm' }
    : { label: 'Within standard', tone: 'ok' };
}
